import type { ExoticComponent, FunctionComponent } from 'react';

import type { AnyPopup, PopupContentType } from '@pressreader/popups-types';
import { CustomError } from '@pressreader/types';

import type { PopupOptions } from './types/popup.options';

class PopupIsAlreadyRegisteredError extends CustomError {}
class PopupNotFoundError extends CustomError {}

interface IPopupComponentProps<TPopup extends AnyPopup = AnyPopup, TPopupResult = void> {
    popup: TPopup;

    onClose(result: TPopupResult, goBack?: boolean, options?: PopupOptions): void;
    onCancel(goBack?: boolean): void;
}

type PopupComponent<TPopup extends AnyPopup = AnyPopup, TResult = void> =
    | ExoticComponent<IPopupComponentProps<TPopup, TResult>>
    | FunctionComponent<IPopupComponentProps<TPopup, TResult>>;

const popups: Record<
    string,
    {
        component: PopupComponent<AnyPopup, unknown>;
        options?: Partial<PopupOptions>;
    }
> = {};

function registerPopupComponent<TPopup extends AnyPopup, TResult = void>(
    name: string,
    component: PopupComponent<TPopup, TResult>,
    options?: PopupOptions,
) {
    if (popups[name]) {
        throw new PopupIsAlreadyRegisteredError(`Popup "${name}" is already reagistered.`);
    }
    popups[name] = { component: component as any, options };
}

function unregisterPopupComponent(name: string) {
    delete popups[name];
}

function getPopupComponent(name: string) {
    const result = popups[name];
    if (!result) {
        throw new PopupNotFoundError(`Popup "${name}" is not found.`);
    }
    return result.component;
}

function getPopupOptions(name: string): Partial<PopupOptions> | null {
    const result = popups[name];
    if (!result) {
        throw new PopupNotFoundError(`Popup "${name}" is not found.`);
    }
    return result.options ?? null;
}

function getPopupContent(name: string): PopupContentType | null {
    const options = getPopupOptions(name);
    return options?.content ?? null;
}

export type { IPopupComponentProps };
export { getPopupComponent, getPopupContent, PopupIsAlreadyRegisteredError, registerPopupComponent, unregisterPopupComponent };
